import React, { useState, useEffect, useRef } from "react";
import Modal from "@mui/material/Modal";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { useIntersectionObserver } from "usehooks-ts";
import { debounce } from "lodash";
import useGet from "../../../hooks/useGet";
import SearchIcon from "@mui/icons-material/Search";

const IngredientsSelectModal = ({
  open,
  onClose,
  selected = [],
  onSave,
}) => {
  // State for pagination and search
  const [pageNo, setPageNo] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [ingredients, setIngredients] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);

  // Set up useGet hook for fetching ingredients
  const {
    data: ingredientsData,
    setParamsObject,
    data: ingredientsQuery,
  } = useGet({
    url: "ingredients/list",
    initialParams: {
      pageNo: 0,
      limit: 10,
      searchQuery: "",
    },
    onSuccess: (response) => {
      const newIngredients = response?.data || [];
      const total_count = response?.totalCount || 0;
      
      // If it's the first page, replace ingredients, otherwise append
      if (pageNo === 0) {
        setIngredients(newIngredients);
      } else {
        setIngredients((prev) => [...prev, ...newIngredients]);
      }
      
      // Calculate how many items we'll have after this update
      const updatedIngredientCount = pageNo === 0 
        ? newIngredients.length 
        : ingredients.length + newIngredients.length;
      
      // Check if there are more ingredients to load by comparing with total_count
      setHasMore(updatedIngredientCount < total_count);
    },
    onError: (err) => {
      console.error("Ingredients fetch error:", err);
    },
  });

  // Set up intersection observer for infinite scrolling
  const { ref } = useIntersectionObserver({
    threshold: 0.5,
    onChange: (isIntersecting) => {
      if (isIntersecting && hasMore && !ingredientsQuery?.isFetching) {
        loadMoreIngredients();
      }
    },
  });

  // Load more ingredients when scrolling down
  const loadMoreIngredients = () => {
    setPageNo((prev) => prev + 1);
    setParamsObject({
      pageNo: pageNo + 1,
      limit: 10,
      searchQuery: searchQuery,
    });
  };

  // Debounced search function
  const debouncedSearch = useRef(
    debounce((query) => {
      setSearchQuery(query);
      setPageNo(0); // Reset to first page when searching
      setParamsObject({
        pageNo: 0,
        limit: 10,
        searchQuery: query,
      });
    }, 500)
  ).current;

  // Handle search input change
  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  // Initialize selected ingredients and manage the single source of truth
  useEffect(() => {
    if (open) {
      // Convert selected array to IDs and keep as single source of truth
      const ids = selected.map(item => item.ingredient_id);
      setSelectedIds(ids);
      setSelectedItems(selected);
    }
  }, [selected, open]);

  // Toggle ingredient selection
  const handleToggle = (id) => {
    const isSelected = selectedIds.includes(id);
    
    if (isSelected) {
      // Remove item
      setSelectedIds(prev => prev.filter(x => x !== id));
      setSelectedItems(prev => prev.filter(item => item.ingredient_id !== id));
    } else {
      // Add item
      const ingredientToAdd = ingredients.find(item => item.ingredient_id === id);
      if (ingredientToAdd) {
        setSelectedIds(prev => [...prev, id]);
        setSelectedItems(prev => [...prev, ingredientToAdd]);
      }
    }
  };

  // Remove an ingredient by clicking its chip - now operates on both state arrays
  const handleRemoveChip = (id, event) => {
    if (event) {
      event.stopPropagation(); // Prevent event bubbling
    }
    setSelectedIds(prev => prev.filter(x => x !== id));
    setSelectedItems(prev => prev.filter(item => item.ingredient_id !== id));
  };

  // Save selected ingredients - now uses the selectedItems directly
  const handleSave = () => {
    onSave(selectedItems);
  };

  // Loading state for the initial load
  const isLoading = ingredientsQuery?.isLoading;
  const isFetching = ingredientsQuery?.isFetching;

  return (
    <Modal open={open} onClose={onClose}>
      <div className="absolute top-1/2 left-1/2 bg-white rounded shadow-lg p-6 min-w-[350px] max-w-[90vw] -translate-x-1/2 -translate-y-1/2">
        <h2 className="text-xl font-semibold mb-4">Select Ingredients</h2>
        
        {/* Search input */}
        <div className="mb-4 relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search ingredients..."
            onChange={handleSearchChange}
            className="pl-10 pr-4 py-2 w-full border rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
        </div>
        
        {/* Selected Ingredients Chips */}
        {selectedItems.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-2">
              {selectedItems.map(item => (
                <div 
                  key={item.ingredient_id} 
                  className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded flex items-center"
                >
                  {item.name}
                  <button 
                    type="button"
                    className="ml-1.5 text-blue-800 hover:text-blue-900"
                    onClick={(e) => handleRemoveChip(item.ingredient_id, e)}
                  >
                    &times;
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="max-h-72 overflow-y-auto mb-4">
          {/* Initial loading state - when no ingredients are loaded yet */}
          {isLoading && ingredients.length === 0 ? (
            <div className="flex justify-center py-8">
              <CircularProgress />
            </div>
          ) : (
            <div className="">
              {ingredients.length > 0 ? (
                <>
                  {/* Render available ingredients */}
                  {ingredients.map((item) => {
                    const isSelected = selectedIds.includes(item.ingredient_id);
                    return (
                      <div
                        key={item.ingredient_id}
                        className={`flex items-center px-4 py-3 border cursor-pointer transition-colors mb-2 ${
                          isSelected
                            ? "bg-blue-50"
                            : "hover:bg-gray-50"
                        }`}
                        onClick={() => handleToggle(item.ingredient_id)}
                        tabIndex={0}
                        role="button"
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          className="mr-3"
                          tabIndex={-1}
                          readOnly
                        />
                        <span className="flex-1 text-gray-800">{item.name}</span>
                      </div>
                    );
                  })}
                  
                  {/* Load more indicator - only visible when there are more items to load */}
                  {hasMore && (
                    <div ref={ref} className="py-2 text-center">
                      {isFetching && (
                        <CircularProgress size={24} className="mt-2" />
                      )}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  No ingredients found
                </div>
              )}
            </div>
          )}
        </div>
        
        <div className="flex justify-end space-x-2">
          <Button onClick={onClose} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            // disabled={selectedIds.length === 0}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default IngredientsSelectModal;
